<template>
	<!-- Check `tournament.externalLink` to make sure people don't see this page in that case. -->
  <section class="section" v-if="!tournament.externalLink">
		<h1 class="title is-size-4">{{ mainTitle }}</h1>
		<h2 class="subtitle">{{ mainSubtitle }}</h2>
		<b-field label="Team Name" v-if="!tournament.individualRegistration" style="margin-bottom: 1rem;">
			<b-input
				v-model="name"
				required
				name="team_name"
				type="text"
				icon="trophy"
				placeholder="Team Name"
			/>
		</b-field>
		<b-field
			label="Gender Group"
			v-if="tournament.genderGroups.length > 1"
			style="margin-bottom: 1rem;"
		>
			<div class="block">
				<b-radio
					v-for="genderSignup in tournament.genderGroups"
					:key="genderSignup"
					v-model="genderGroup"
					:native-value="genderSignup"
				>
					{{ genderSignup }}
				</b-radio>
			</div>
		</b-field>
		<b-field
			label="Number of Players"
			v-if="tournament.numberOfPlayers.length > 1"
			style="margin-bottom: 1rem;"
		>
			<div class="block">
				<b-radio
					v-for="numPlayers in tournament.numberOfPlayers"
					:key="numPlayers"
					v-model="numberOfPlayers"
					:native-value="numPlayers"
				>
					{{ numPlayers }}s
				</b-radio>
			</div>
		</b-field>
		<b-field
			label="Division"
			v-if="divisionChoices.length > 1"
			style="margin-bottom: 1rem;"
		>
			<div class="block">
				<b-radio
					v-for="div in divisionChoices"
					:key="div"
					v-model="division"
					:native-value="div"
				>
					{{ div }}
				</b-radio>
			</div>
		</b-field>
		<div v-if="genderGroup && division && numberOfPlayers">
			<signup-player-detail
				:key="0"
				:label="firstPlayerLabel"
				:plural-label="pluralize(firstPlayerLabel)"
				:player="captain"
				phone-required
				@update:player="updateCaptain"
			/>
			<signup-player-detail
				v-for="(player, index) in players"
				:key="player.vueKey"
				:label="indexToPlayer(index + 1)"
				:player="player"
				:plural-label="pluralize(indexToPlayer(index + 1))"
				is-removable
				@remove:player="removePlayer(index)"
				@update:player="updatePlayer(index, $event)"
			/>
			<button
				class="button is-block"
				style="margin-top: 1rem; margin-bottom: 0.5rem;"
				v-if="canAddPlayer"
				@click="addPlayer">
					<b-icon
						icon="plus"
						size="is-small">
					</b-icon>
					Add Player
			</button>
			<p v-if="canAddPlayer" class="has-text-left" style="margin-bottom: 1rem;">
				You can optionally add up to {{ playersRemaining }} more player{{ playersRemaining == 1 ? '' : 's' }}. Make sure
				you have {{ playersRemaining == 1 ? 'his/her' : 'their' }} name, email address, and optionally phone number.
				Adding players allows them to receive any tournament updates directly via email (and possibly phone) and makes
				it easier to check in on the day of the tournament.
			</p>
			<signup-errors :errors="errors" :index-to-player-name="indexToPlayerName" :first-player-label="firstPlayerLabel" :pluralize="pluralize" />
			<button
				type="button"
				:class="['button', 'is-rounded', 'is-primary', 'is-medium', {'is-loading': signupInProgress}]"
				@click="signup">
					Signup
			</button>
		</div>
  </section>
</template>

<script>
import PlayerAdder from '@/components/PlayerAdder.vue'
import SignupErrors from '@/components/SignupErrors.vue'
import SignupPlayerDetail from '@/components/SignupPlayerDetail.vue';
import tournamentRetriever from '@/mixins/tournamentRetriever.js'

export default {
  name: 'TournamentSignup',
	mixins: [tournamentRetriever],
	components: {
		SignupPlayerDetail,
		SignupErrors,
	},
  computed: {
		firstPlayerLabel() {
			if (this.tournament.individualRegistration) {
				return "Player";
			} if (this.numberOfPlayers > 2) {
				return "Captain";
			} if (this.numberOfPlayers == 2) {
				return "First Player";
			}
			return "Player";
		},
		playersRemaining() {
			if (this.tournament.individualRegistration) return 0;
			return this.numberOfPlayers - this.players.length - 1;
		},
		canAddPlayer() {
			if (this.tournament.individualRegistration) return false;
			return this.players.length + 1 < this.numberOfPlayers;
		},
		registrationType() {
			return this.tournament.individualRegistration ? 'individual' : 'team';
		},
		divisionChoices() {
			let divisions = this.tournament.divisions
			if (divisions.indexOf("All") >= 0) {
				return ["Rec", "Int", "Int/Adv", "Adv", "Open", "12U", "14U", "16U", "18U"];
			}
			return divisions
		},
		mainTitle() {
			return `Registering for ${this.tournament.name}`;
		},
		mainSubtitle() {
			const option = this.tournament.individualRegistration ? 'individual' : 'team';
			return `One ${option} registration (\$${this.tournament.price})`;
		}
  },
  methods: {
		indexToPlayer(index) {
			if (index == 0) return this.firstPlayerLabel;
			return `${this.indexToPlayerName[index]} Player`;
		},
		signup() {
			if (this.signupInProgress) return;
			let data = {
				tournament: this.tournament.id,
				genderGroup: this.genderGroup,
				division: this.division,
				numberOfPlayers: this.numberOfPlayers
			};
			if (this.registrationType == 'team') {
				data.name = this.name;
				data.captain = this.captain;
				data.players = this.players;
			} else {
				data.player = this.captain;
			}
      let promise = this.axios.post(`${this.$apiRoot}/${this.registrationType}-register/`, data);
      this.signupInProgress = true;
      promise.then(result => {
        this.signupInProgress = false;
        this.errors = {};
        this.$router.push({
					name: "tournament-signup-pay",
					params: {
						tournamentId: this.tournament.id,
						registrationId: result.data.id
					}
				});
      });
      promise.catch(error => {
        this.signupInProgress = false;
        this.errors = error.response.data;
      });
		},
		pluralize(label) {
			return `${label}'s`;
		},
		addPlayer() {
			this.players.push({
				name: '',
				email: '',
				phoneNumber: '',
				vueKey: this.nextPlayerKey++
			});
		},
		updateCaptain([field, value]) {
			this.captain = {...this.captain, [field]: value};
		},
		removePlayer(index) {
			this.players.splice(index, 1);
		},
		updatePlayer(index, [field, value]) {
			this.players.splice(index, 1, {...this.players[index], [field]: value});
		}
	},
  data() {
		return {
			// Only used right now if it's a team registration.
			name: '',
			genderGroup: '',
			division: '',
			numberOfPlayers: 0,
			captain: {
				name: '',
				email: '',
				phoneNumber: ''
			},
			players: [],
			signupInProgress: false,
      // If set, came from the server upon signing up.
      errors: {},
		}
	},
	created() {
		this.indexToPlayerName = {
			0: "First",
			1: "Second",
			2: "Third",
			3: "Fourth",
			4: "Fifth",
			5: "Sixth",
			// Only uncomment below if these actually end up being needed.
			// 6: "Seventh",
			// 7: "Eighth",
			// 8: "Ninth",
		}
		this.nextPlayerKey = 1;
		let genderGroups = this.tournament.genderGroups;
		let divisions = this.divisionChoices;
		let numberOfPlayers = this.tournament.numberOfPlayers;
		if (genderGroups.length == 1) this.genderGroup = genderGroups[0];
		if (divisions.length == 1) this.division = divisions[0];
		if (numberOfPlayers.length == 1) this.numberOfPlayers = numberOfPlayers[0];
	}
}
</script>

<style scoped lang="scss">
h1.title {
  margin-top: 1.5rem;
}
.section {
	@media (min-width: 650px) {
		max-width: 650px;
		margin: 0 auto;
	}
}
</style>